import { cn } from '@perch/ui-tokens';

interface RouteDisplayProps {
  segments: Array<{ origin: string; destination: string }>;
  tripType?: 'one_way' | 'round_trip' | 'multi_city';
  className?: string;
}

const codeClasses = 'font-mono font-semibold tracking-[0.03em] uppercase text-sm';

/**
 * Simplified route display for admin tables. Always inline/sm size.
 *
 * - One-way: SFO → NRT
 * - Roundtrip: SFO ↔ NRT
 * - Multi-city: SFO → ORD → NRT
 */
export function RouteDisplay({
  segments,
  tripType = 'one_way',
  className,
}: RouteDisplayProps) {
  if (segments.length === 0) return null;

  const origin = segments[0].origin;
  const destination = segments[segments.length - 1].destination;

  if (tripType === 'round_trip') {
    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        <span className={codeClasses}>{origin}</span>
        <span className="text-stone-400 dark:text-stone-500" aria-hidden="true">↔</span>
        <span className={codeClasses}>{destination}</span>
      </span>
    );
  }

  if (tripType === 'multi_city' && segments.length > 1) {
    // Build ordered list of unique airport codes
    const airports: string[] = [];
    for (const seg of segments) {
      if (airports.length === 0 || airports[airports.length - 1] !== seg.origin) {
        airports.push(seg.origin);
      }
      airports.push(seg.destination);
    }

    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        {airports.map((code, i) => (
          <span key={`${code}-${i}`} className="inline-flex items-center gap-1.5">
            <span className={codeClasses}>{code}</span>
            {i < airports.length - 1 && (
              <span className="text-stone-400 dark:text-stone-500" aria-hidden="true">→</span>
            )}
          </span>
        ))}
      </span>
    );
  }

  // One-way (default)
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span className={codeClasses}>{origin}</span>
      <span className="text-stone-400 dark:text-stone-500" aria-hidden="true">→</span>
      <span className={codeClasses}>{destination}</span>
    </span>
  );
}
